/**
 * Lobby-only hotkeys: F Forge, U Forge › Enhance (the legacy
 * `temper` / `sets` ids are aliases of the forge tabs).
 *
 * The in-run menu keymap (hud/panels/hotkeys.ts) cannot take F (an ability key in a run), so
 * these live on the meta side and only act while the hub (or a meta screen) is showing outside
 * a run. Same toggle rule as the menu keys: pressing the key of the open screen goes back.
 * `hubHotkey` is the pure routing table (unit-tested); `installHubHotkeys` adds one listener.
 */
import { ROUTES, type RouteId } from './router';

export interface HubKeyDef {
  /** KeyboardEvent.code (layout independent). */
  code: string;
  label: string;
  route: RouteId;
}

export const HUB_KEYS: readonly HubKeyDef[] = [
  { code: 'KeyF', label: 'F', route: ROUTES.forge },
  { code: 'KeyU', label: 'U', route: ROUTES.temper },
];

/** Hint label of a route's hub key ("" when unbound). */
export function hubKeyLabel(route: RouteId): string {
  return HUB_KEYS.find((k) => k.route === route)?.label ?? '';
}

export interface HubKeyLike {
  code: string;
  altKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  repeat?: boolean;
}

export interface HubKeyState {
  /** A key is running (menu keys own the keyboard; F is an ability there). */
  inRun: boolean;
  /** A meta screen (hub or other) is mounted. */
  screenOpen: boolean;
  typing: boolean;
  modalOpen: boolean;
}

/** The route a keydown opens in the hub, or null (not ours). */
export function hubHotkey(e: HubKeyLike, s: HubKeyState): RouteId | null {
  if (s.inRun || !s.screenOpen || s.typing || s.modalOpen) return null;
  if (e.repeat || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return null;
  return HUB_KEYS.find((k) => k.code === e.code)?.route ?? null;
}

export interface HubHotkeyTarget {
  state(typing: boolean): HubKeyState;
  /** Open `route`, or go back if it is already on top. */
  toggle(route: RouteId): void;
}

let installed = false;

function isTyping(t: EventTarget | null): boolean {
  const e = t as HTMLElement | null;
  if (!e || typeof e.tagName !== 'string') return false;
  return e.tagName === 'INPUT' || e.tagName === 'TEXTAREA' || e.tagName === 'SELECT' || e.isContentEditable === true;
}

/** Install the hub key listener once. */
export function installHubHotkeys(target: HubHotkeyTarget): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const codes = new Set(HUB_KEYS.map((k) => k.code));
  window.addEventListener('keydown', (e) => {
    if (!codes.has(e.code) || e.defaultPrevented) return;
    const route = hubHotkey(e, target.state(isTyping(e.target)));
    if (!route) return;
    e.preventDefault();
    target.toggle(route);
  });
}
